import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AddNewFooter } from '@/components/add-new-footer';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import {
  Dispatch,
  EditOrderGuideActions,
  Item,
} from '@/hooks/use-edit-order-guide-reducer';
import {
  LIST_ITEM_SEPARATOR_HEIGHT,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import {
  ListItem,
  ListItemSeparator,
  ListOption,
} from '@/components/list-item';
import { OrderGuideActionSheetNameInput } from './order-guide-action-sheet-name-input';
import {
  SectionList,
  SectionListData,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { ToastOptions, Type } from '@/components/toast';
import { validatePartitionName } from '@/utils/order-guide';

enum Route {
  AddPartition = 'ADD_PARTITION',
  SelectPartition = 'SELECT_PARTITION',
}

export const createItemsAlreadyExistToast = (
  existingItems: Item[],
  groupName: string,
): ToastOptions => ({
  content: i18n.t('toast.items_already_exist', {
    groupName,
    items: existingItems
      .map((i) =>
        [i.itemListOption.title, i.itemListOption.metadata?.packDisplayString]
          .filter(Boolean)
          .join(' '),
      )
      .join(', '),
  }),
  type: Type.PRIMARY,
});

interface Props {
  dispatch: Dispatch;
  onClose: () => void;
  onSelect: (option: ListOption) => void;
  sections: SectionListData<ListOption>[];
  selectPartitionTitle: string;
}

export const MoveDuplicateItemsActionSheet: React.FC<Props> = ({
  dispatch,
  onSelect,
  onClose,
  sections,
  selectPartitionTitle,
}) => {
  const windowDimensions = useWindowDimensions();
  const [styles] = useStyles();
  const [inputError, setInputError] = useState('');

  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.85);

  return (
    <CombinedActionSheet
      contentStyle={[styles.base, { maxHeight: actionSheetMaxHeight }]}
      initialRoute={Route.SelectPartition}
      onClose={onClose}
      onRouteChange={() => {
        setInputError('');
      }}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case Route.AddPartition:
            return (
              <View style={styles.actionSheetInputContainer}>
                <OrderGuideActionSheetNameInput
                  inputError={inputError}
                  nameInputLabel={i18n.t('group_name')}
                  onChangeText={() => {
                    setInputError('');
                  }}
                  onSave={(partitionName) => {
                    dispatch((actionDispatch, getState) => {
                      const partitionNames = getState().partitions.reduce<
                        string[]
                      >((acc, p) => {
                        if (p.partitionName) {
                          acc.push(p.partitionName);
                        }
                        return acc;
                      }, []);
                      if (
                        !validatePartitionName(partitionName, partitionNames)
                      ) {
                        setInputError(i18n.t('group_name_error'));
                      } else {
                        actionDispatch({
                          payload: {
                            partitionName,
                            stayOnSelectedPartition: true,
                          },
                          type: EditOrderGuideActions.ADD_PARTITION,
                        });
                        jumpTo(Route.SelectPartition);
                      }
                    });
                  }}
                  saveButtonText={i18n.t('create')}
                />
              </View>
            );
          case Route.SelectPartition:
            return (
              <SectionList
                alwaysBounceVertical={false}
                contentInsetAdjustmentBehavior="automatic"
                ItemSeparatorComponent={ListItemSeparator}
                ListFooterComponent={() => (
                  <>
                    <ListItemSeparator />
                    <AddNewFooter
                      onSelect={() => jumpTo(Route.AddPartition)}
                      size="small"
                      title={i18n.t('new_group')}
                    />
                  </>
                )}
                renderItem={({ item }) => {
                  return <ListItem item={item} onSelect={onSelect} />;
                }}
                renderSectionHeader={({ section }) => {
                  return section.key ? (
                    <Text style={[TYPOGRAPHY.listTitle, styles.groupListTitle]}>
                      {section.key}
                    </Text>
                  ) : null;
                }}
                sections={sections}
                testID="move-duplicate-items-list"
              />
            );
        }
      }}
      routes={{
        [Route.AddPartition]: {
          previousKey: Route.SelectPartition,
          showSmallLayoutBackButton: true,
          showSmallLayoutTitle: true,
          title: i18n.t('new_group'),
        },
        [Route.SelectPartition]: {
          previousKey: null,
          showSmallLayoutTitle: true,
          title: selectPartitionTitle,
        },
      }}
      titleStyle={styles.title}
    />
  );
};

const useStyles = createStyles((theme) => ({
  actionSheetInputContainer: {
    paddingHorizontal: SCREEN_PADDING,
  },
  base: {
    paddingHorizontal: 0,
  },
  groupListTitle: {
    backgroundColor: theme.colors.white,
    color: theme.colors.inactiveText,
    // Hack to hide the list item separator above the title
    marginTop: -LIST_ITEM_SEPARATOR_HEIGHT,
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: 20,
    textTransform: 'uppercase',
  },
  title: {
    paddingHorizontal: SCREEN_PADDING,
  },
}));
